import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { UpdateParentInformationContent } from './content'
import { UpdateParentInformationControls } from './controls'

export enum Input {
  FirstName = 'first_name',
  LastName = 'last_name',
  Email = 'email',
  Phone = 'phone',
}

export const updateParentInformationSchema = z.object({
  [Input.FirstName]: z.string()
    .min(1, { message: 'First name is required.' })
    .max(255, { message: 'First name must be less than 255 characters.' }),
  [Input.LastName]: z.string()
    .min(1, { message: 'Last name is required.' })
    .max(255, { message: 'Last name must be less than 255 characters.' }),
  [Input.Email]: z.string()
    .email({ message: 'Invalid email address.' })
    .max(255, { message: 'Email must be less than 255 characters.' }),
  [Input.Phone]: z.string()
    .min(1, { message: 'Phone is required.' })
    .max(32, { message: 'Phone must be less than 32 characters.' }),
})

export type UpdateParentInformationSchema = z.infer<typeof updateParentInformationSchema>

type UpdateParentInformationFormContextType = {
  onSubmit: (values: UpdateParentInformationSchema) => void
  onCancel: () => void
  isLoading?: boolean
}

const UpdateParentInformationFormContext = createContext<UpdateParentInformationFormContextType | undefined>(undefined)

type UpdateParentInformationFormProps = UpdateParentInformationFormContextType & {
  children?: ReactNode
  defaultValues?: Partial<UpdateParentInformationSchema>
}

export function UpdateParentInformationForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: UpdateParentInformationFormProps) {
  const context: UpdateParentInformationFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <UpdateParentInformationFormContext.Provider value={context}>
      <Form<UpdateParentInformationSchema, typeof updateParentInformationSchema>
        schema={updateParentInformationSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </UpdateParentInformationFormContext.Provider>
  )
}

export function useUpdateParentInformationForm() {
  const context = useContext(UpdateParentInformationFormContext)
  if (context === undefined) {
    throw new Error('useUpdateParentInformationForm must be used within an UpdateParentInformationFormProvider')
  }
  return context
}

// Compound components
UpdateParentInformationForm.Content = UpdateParentInformationContent
UpdateParentInformationForm.Controls = UpdateParentInformationControls
